import React, { useState, useEffect } from 'react';
import { 
  X, 
  Building2, 
  Mail, 
  Phone, 
  MapPin, 
  Globe, 
  Share2, 
  FileText, 
  Check, 
  Save, 
  Image, 
  Link2, 
  CreditCard, 
  Eye, 
  EyeOff, 
  Award,
  Trash2, 
  ShieldCheck 
} from 'lucide-react';
import { BusinessSettings } from '../types';
import { getBusinessSettings, saveBusinessSettings } from '../utils/businessSettings';
import { DeleteAccountModal } from './DeleteAccountModal';
import { JURISDICTION_LIST, getJurisdictionPreset } from '../utils/jurisdictionPresets';
import { PWACheckUpdatesButton } from './PWAUpdateBanner';
import { TrustDashboard } from './TrustDashboard';
import { CohortGovernancePanel } from './CohortGovernancePanel';
import { CohortOnboardingModal } from './CohortOnboardingModal';
import { MultiProviderBYOKCard } from './MultiProviderBYOKCard';
import { MultiProviderBYOKModal } from './MultiProviderBYOKModal';
import { Key } from 'lucide-react';
import { 
  AiProviderId, 
  loadByokVault, 
  saveByokVault, 
  AI_PROVIDERS_CONFIG 
} from '../utils/byokService';

interface BusinessSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: (settings: BusinessSettings) => void;
  isPrivacyMode?: boolean;
  onTogglePrivacyMode?: () => void;
  showToast?: (msg: string, type?: 'info' | 'success' | 'warning' | 'error') => void;
  defaultTab?: 'profile' | 'byok' | 'cohort' | 'trust_audit' | 'account';
}

export function BusinessSettingsModal({ 
  isOpen, 
  onClose, 
  onSaved,
  isPrivacyMode = false,
  onTogglePrivacyMode,
  showToast,
  defaultTab = 'profile'
}: BusinessSettingsModalProps) {
  const [activeTab, setActiveTab] = useState<'profile' | 'byok' | 'cohort' | 'trust_audit' | 'account'>(defaultTab);
  const [formData, setFormData] = useState<BusinessSettings>(getBusinessSettings());
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCohortModalOpen, setIsCohortModalOpen] = useState(false);
  const [showByokModal, setShowByokModal] = useState(false);
  const [byokInitialProvider, setByokInitialProvider] = useState<AiProviderId>('gemini');
  const [activeAiProvider, setActiveAiProvider] = useState<AiProviderId>(() => loadByokVault().activeProvider || 'gemini');

  useEffect(() => {
    const handleVaultSync = () => {
      const v = loadByokVault();
      setActiveAiProvider(v.activeProvider || 'gemini');
    };
    window.addEventListener('azuraos_byok_updated', handleVaultSync);
    return () => window.removeEventListener('azuraos_byok_updated', handleVaultSync);
  }, []);

  const handleSwitchAiProvider = (pid: AiProviderId) => {
    const v = loadByokVault();
    const updated = { ...v, activeProvider: pid };
    saveByokVault(updated);
    setActiveAiProvider(pid);
    if (showToast) {
      showToast(`Operational AI Engine active provider set to ${AI_PROVIDERS_CONFIG[pid].name}`, 'success');
    }
  };

  useEffect(() => {
    if (isOpen) {
      setFormData(getBusinessSettings());
      setSavedSuccess(false);
      setActiveTab(defaultTab);
    }
  }, [isOpen, defaultTab]);

  if (!isOpen) return null;

  const handleChange = (key: keyof BusinessSettings, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveBusinessSettings(formData);
    setSavedSuccess(true);
    if (onSaved) {
      onSaved(formData);
    }
    setTimeout(() => {
      setSavedSuccess(false);
      onClose();
    }, 800);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-[#0C0E0F]/85 backdrop-blur-md animate-fade-in">
      <div className="absolute inset-0 cursor-pointer" onClick={onClose} />

      <div 
        className="relative my-auto w-full max-w-3xl h-[94vh] sm:h-auto sm:max-h-[90vh] rounded-t-3xl sm:rounded-3xl bg-[#151819] border-t sm:border border-[#2A2F31] shadow-2xl transition-all duration-300 flex flex-col overflow-hidden z-10"
        onClick={(e) => e.stopPropagation()}
      >
        
        {/* MODAL HEADER */}
        <div className="flex items-center justify-between border-b border-[#1F2426] p-4 sm:p-5 bg-[#0C0E0F] shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#14B8A6]/15 border border-[#14B8A6]/35 flex items-center justify-center text-[#14B8A6] shrink-0">
              <Building2 size={20} />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-display font-bold text-white leading-tight">Settings &amp; Data Governance</h3>
              <p className="text-xs text-gray-400 mt-0.5">Configure business branding, invoice settings, and data boundaries</p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close Settings"
            className="p-2 rounded-xl text-gray-400 hover:text-white bg-[#151819] hover:bg-[#2A2F31] border border-[#252C2F] transition-colors cursor-pointer shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        {/* SETTINGS TABS SELECTOR - ROBUST PILLS WITH NO OVERLAPS */}
        <div className="flex items-center gap-2 border-b border-[#1F2426] px-4 sm:px-5 py-2.5 bg-[#0C0E0F] overflow-x-auto scrollbar-none shrink-0">
          <button
            type="button"
            onClick={() => setActiveTab('profile')}
            className={`px-4 py-2.5 rounded-xl text-xs font-bold transition flex items-center gap-2 cursor-pointer shrink-0 whitespace-nowrap ${
              activeTab === 'profile'
                ? 'bg-[#14B8A6] text-black shadow-md font-extrabold'
                : 'bg-[#151819] text-gray-300 hover:text-white border border-[#252C2F]'
            }`}
          >
            <Building2 size={15} />
            <span>Sender Profile &amp; Branding</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('byok')}
            className={`px-4 py-2.5 rounded-xl text-xs font-bold transition flex items-center gap-2 cursor-pointer shrink-0 whitespace-nowrap ${
              activeTab === 'byok'
                ? 'bg-indigo-600 text-white shadow-md font-extrabold'
                : 'bg-[#151819] text-indigo-300 hover:text-indigo-200 border border-indigo-500/30'
            }`}
          >
            <Key size={15} />
            <span>AI BYOK Vault (8 Providers)</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('cohort')}
            className={`px-4 py-2.5 rounded-xl text-xs font-bold transition flex items-center gap-2 cursor-pointer shrink-0 whitespace-nowrap ${
              activeTab === 'cohort'
                ? 'bg-teal-500 text-black shadow-md font-extrabold'
                : 'bg-[#151819] text-teal-300 hover:text-teal-200 border border-teal-500/30'
            }`}
          >
            <Award size={15} />
            <span>Cohort &amp; Accelerator Sublicense</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('trust_audit')}
            className={`px-4 py-2.5 rounded-xl text-xs font-bold transition flex items-center gap-2 cursor-pointer shrink-0 whitespace-nowrap ${
              activeTab === 'trust_audit'
                ? 'bg-teal-500 text-black shadow-md font-extrabold'
                : 'bg-[#151819] text-gray-300 hover:text-white border border-[#252C2F]'
            }`}
          >
            <ShieldCheck size={15} />
            <span>Trust Dashboard (Cloud vs Local Audit)</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('account')}
            className={`px-4 py-2.5 rounded-xl text-xs font-bold transition flex items-center gap-2 cursor-pointer shrink-0 whitespace-nowrap ${
              activeTab === 'account'
                ? 'bg-[#14B8A6] text-black shadow-md font-extrabold'
                : 'bg-[#151819] text-gray-300 hover:text-white border border-[#252C2F]'
            }`}
          >
            <Trash2 size={15} />
            <span>App Updates &amp; Deletion</span>
          </button>
        </div>

        {/* MODAL BODY WITH AMPLE PADDING */}
        <div className="overflow-y-auto flex-1 p-4 sm:p-6 pb-28 sm:pb-8 space-y-5 scrollbar-thin scrollbar-thumb-gray-800">

          {/* TAB 1: SENDER PROFILE & BRANDING */}
          {activeTab === 'profile' && (
            <form onSubmit={handleSubmit} className="space-y-4 text-xs">
              {savedSuccess && (
                <div className="p-3.5 bg-[#14B8A6]/15 border border-[#14B8A6]/40 text-[#14B8A6] font-bold rounded-xl flex items-center gap-2.5 animate-slide-in shadow-lg">
                  <Check size={18} />
                  <span>Business profile &amp; invoice branding saved successfully!</span>
                </div>
              )}

              {/* GLOBAL PRIVACY MODE TOGGLE */}
              <div className="p-4 sm:p-5 bg-[#0C0E0F] border border-[#2A2F31] rounded-2xl flex items-center justify-between gap-4 shadow-inner">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-[#14B8A6]/15 border border-[#14B8A6]/30 flex items-center justify-center text-[#14B8A6]">
                      {isPrivacyMode ? <EyeOff size={15} /> : <Eye size={15} />}
                    </div>
                    <span className="font-bold text-white text-xs sm:text-sm">Global Privacy Mode (Public Screen Protection)</span>
                  </div>
                  <p className="text-xs text-gray-400 leading-relaxed max-w-xl">
                    Masks sensitive financial figures (booking prices, total revenue, expenses, and ledger entries) with a blur effect when working in public spaces or presenting your screen.
                  </p>
                </div>

                {onTogglePrivacyMode && (
                  <button
                    type="button"
                    onClick={onTogglePrivacyMode}
                    className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      isPrivacyMode ? 'bg-[#14B8A6]' : 'bg-[#2A2F31]'
                    }`}
                    title={isPrivacyMode ? 'Disable Privacy Mode' : 'Enable Privacy Mode'}
                  >
                    <span
                      className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-black shadow ring-0 transition duration-200 ease-in-out ${
                        isPrivacyMode ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                )}
              </div>

              {/* COMPANY NAME & SENDER NAME */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-3.5 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-1.5">
                  <label className="block text-gray-300 font-bold uppercase text-[10px]">Company / Studio Name</label>
                  <div className="relative">
                    <Building2 size={15} className="absolute left-3.5 top-3 text-gray-500" />
                    <input
                      type="text"
                      required
                      placeholder="e.g. RiseUp Digital Studio"
                      value={formData.companyName}
                      onChange={(e) => handleChange('companyName', e.target.value)}
                      className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl pl-9 pr-3 py-2.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-medium text-xs sm:text-sm"
                    />
                  </div>
                </div>

                <div className="p-3.5 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-1.5">
                  <label className="block text-gray-300 font-bold uppercase text-[10px]">Founder / Owner Name</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Sarah Connor"
                    value={formData.senderName}
                    onChange={(e) => handleChange('senderName', e.target.value)}
                    className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-3.5 py-2.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-medium text-xs sm:text-sm"
                  />
                </div>
              </div>

              {/* Company Logo URL */}
              <div className="p-4 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-2">
                <label className="block text-gray-300 font-bold uppercase text-[10px] flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-[#14B8A6]">
                    <Image size={14} /> Company Logo Image URL
                  </span>
                  <span className="text-[10px] text-gray-500 font-normal">Displays at the top of client invoices</span>
                </label>
                <div className="flex gap-3 items-center">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      placeholder="e.g. https://yourdomain.com/logo.png or image URL"
                      value={formData.logoUrl || ''}
                      onChange={(e) => handleChange('logoUrl', e.target.value)}
                      className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-3.5 py-2.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-medium text-xs"
                    />
                  </div>
                  {formData.logoUrl && (
                    <div className="w-11 h-11 rounded-lg border border-[#2A2F31] bg-white p-1 flex items-center justify-center shrink-0 overflow-hidden shadow">
                      <img
                        src={formData.logoUrl}
                        alt="Logo Preview"
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          (e.target as HTMLElement).style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-3.5 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-1.5">
                  <label className="block text-gray-300 font-bold uppercase text-[10px]">Business Email</label>
                  <div className="relative">
                    <Mail size={15} className="absolute left-3.5 top-3 text-gray-500" />
                    <input
                      type="email"
                      required
                      placeholder="accounts@yourstudio.com"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl pl-9 pr-3 py-2.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-medium text-xs sm:text-sm"
                    />
                  </div>
                </div>

                <div className="p-3.5 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-1.5">
                  <label className="block text-gray-300 font-bold uppercase text-[10px]">Phone Number</label>
                  <div className="relative">
                    <Phone size={15} className="absolute left-3.5 top-3 text-gray-500" />
                    <input
                      type="text"
                      placeholder="e.g. +1 555 012 3456"
                      value={formData.phone || ''}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl pl-9 pr-3 py-2.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-medium text-xs sm:text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="p-3.5 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-1.5">
                <label className="block text-gray-300 font-bold uppercase text-[10px]">Physical / Business Address</label>
                <div className="relative">
                  <MapPin size={15} className="absolute left-3.5 top-3 text-gray-500" />
                  <input
                    type="text"
                    placeholder="e.g. 123 Main Street, Suite 100, City, Country"
                    value={formData.address || ''}
                    onChange={(e) => handleChange('address', e.target.value)}
                    className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl pl-9 pr-3 py-2.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-medium text-xs sm:text-sm"
                  />
                </div>
              </div>

              {/* Payment Link & Gateway Section */}
              <div className="p-4 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-3.5">
                <div className="flex items-center gap-2 text-[#14B8A6] font-bold uppercase text-xs">
                  <CreditCard size={15} /> Payment Instructions for Clients
                </div>

                <div>
                  <label className="block text-gray-300 font-bold uppercase text-[10px] mb-1">Direct Online Payment Link (Stripe / PayPal / Yoco / PayFast / Link)</label>
                  <div className="relative">
                    <Link2 size={15} className="absolute left-3.5 top-3 text-gray-500" />
                    <input
                      type="url"
                      placeholder="e.g. https://pay.stripe.com/me or https://paypal.me/yourname"
                      value={formData.paymentLink || ''}
                      onChange={(e) => handleChange('paymentLink', e.target.value)}
                      className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl pl-9 pr-3 py-2.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-medium text-xs"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-300 font-bold uppercase text-[10px] mb-1">EFT / Direct Bank Account Details (Optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. Bank: FNB | Account: 62819283741 | Branch: 250655 | Ref: [Invoice #]"
                    value={formData.bankDetails || ''}
                    onChange={(e) => handleChange('bankDetails', e.target.value)}
                    className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-3.5 py-2.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-medium text-xs"
                  />
                </div>
              </div>

              {/* Regional Jurisdiction & Tax Configuration Section */}
              <div className="p-4 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-3.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-[#14B8A6] font-bold uppercase text-xs">
                    <Globe size={15} /> Regional Jurisdiction &amp; Tax Presets
                  </div>
                  <span className="text-[10px] text-teal-300 bg-teal-500/10 px-2 py-0.5 rounded font-mono">Global Pilot Ready</span>
                </div>

                <div>
                  <label className="block text-gray-300 font-bold uppercase text-[10px] mb-1">Operating Country / Jurisdiction</label>
                  <select
                    value={formData.jurisdiction || 'ZA'}
                    onChange={(e) => {
                      const jurId = e.target.value;
                      const preset = getJurisdictionPreset(jurId);
                      setFormData(prev => ({
                        ...prev,
                        jurisdiction: jurId,
                        currencySymbol: preset.currencySymbol,
                        currencyCode: preset.currencyCode,
                        vatRate: preset.vatRate,
                        taxAuthorityLabel: preset.taxAuthorityLabel,
                        taxNumberLabel: preset.taxNumberLabel,
                        invoiceTypeLabel: preset.invoiceTypeLabel,
                        fiscalYearEndMonth: preset.fiscalYearEndMonth
                      }));
                    }}
                    className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-3.5 py-2.5 text-white font-medium text-xs sm:text-sm focus:outline-none focus:border-[#14B8A6]"
                  >
                    {JURISDICTION_LIST.map((j) => (
                      <option key={j.id} value={j.id}>
                        {j.flag} {j.name} ({j.currencyCode} - {j.taxAuthorityLabel.split(' ')[0]})
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-teal-400/90 mt-1.5 font-mono">
                    {getJurisdictionPreset(formData.jurisdiction).description}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-2 border-t border-[#1F2426]">
                  <div>
                    <label className="block text-gray-300 font-bold uppercase text-[10px] mb-1">Primary Currency</label>
                    <select
                      value={`${formData.currencySymbol || 'R'}|${formData.currencyCode || 'ZAR'}`}
                      onChange={(e) => {
                        const [sym, code] = e.target.value.split('|');
                        setFormData(prev => ({ ...prev, currencySymbol: sym, currencyCode: code }));
                      }}
                      className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-3 py-2.5 text-white font-medium text-xs focus:outline-none focus:border-[#14B8A6]"
                    >
                      <option value="R|ZAR">ZAR (R - South African Rand)</option>
                      <option value="$|USD">USD ($ - US Dollar)</option>
                      <option value="€|EUR">EUR (€ - Euro)</option>
                      <option value="£|GBP">GBP (£ - British Pound)</option>
                      <option value="₹|INR">INR (₹ - Indian Rupee)</option>
                      <option value="A$|AUD">AUD (A$ - Australian Dollar)</option>
                      <option value="C$|CAD">CAD (C$ - Canadian Dollar)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-gray-300 font-bold uppercase text-[10px] mb-1">VAT / Tax Rate (%)</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="50"
                        placeholder="15"
                        value={formData.vatRate !== undefined ? formData.vatRate : 15}
                        onChange={(e) => setFormData(prev => ({ ...prev, vatRate: parseFloat(e.target.value) || 0 }))}
                        className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-3 py-2.5 text-white font-medium text-xs focus:outline-none focus:border-[#14B8A6]"
                      />
                      <span className="text-gray-300 font-bold text-xs">%</span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-300 font-bold uppercase text-[10px] mb-1">Tax Authority / Filing Dept Label</label>
                  <input
                    type="text"
                    placeholder="e.g. SARS (South Africa), IRS (USA), HMRC (UK), GST Dept"
                    value={formData.taxAuthorityLabel || ''}
                    onChange={(e) => handleChange('taxAuthorityLabel', e.target.value)}
                    className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-3.5 py-2.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-medium text-xs"
                  />
                </div>
              </div>

              {/* Tax Number & Company Registration */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-3.5 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-1.5">
                  <label className="block text-gray-300 font-bold uppercase text-[10px]">Tax / VAT Registration #</label>
                  <div className="relative">
                    <FileText size={15} className="absolute left-3.5 top-3 text-gray-500" />
                    <input
                      type="text"
                      placeholder="e.g. VAT 4901928374 or Tax ID"
                      value={formData.taxNumber || ''}
                      onChange={(e) => handleChange('taxNumber', e.target.value)}
                      className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl pl-9 pr-3 py-2.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-medium text-xs sm:text-sm"
                    />
                  </div>
                </div>

                <div className="p-3.5 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-1.5">
                  <label className="block text-gray-300 font-bold uppercase text-[10px] flex items-center justify-between">
                    <span>Company Registration #</span>
                    <span className="text-[10px] text-gray-500 font-normal lowercase">(optional)</span>
                  </label>
                  <div className="relative">
                    <Building2 size={15} className="absolute left-3.5 top-3 text-gray-500" />
                    <input
                      type="text"
                      placeholder="e.g. 2024/123456/07 (CIPC / Reg)"
                      value={formData.registrationNumber || ''}
                      onChange={(e) => handleChange('registrationNumber', e.target.value)}
                      className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl pl-9 pr-3 py-2.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-medium text-xs sm:text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Invoice Disclaimer / Footer Note */}
              <div className="p-3.5 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-1.5">
                <label className="block text-gray-300 font-bold uppercase text-[10px]">Standard Invoice Footer Note</label>
                <textarea
                  rows={2}
                  placeholder="e.g. Thank you for your business! Payment due within 7 days."
                  value={formData.invoiceNotes || ''}
                  onChange={(e) => handleChange('invoiceNotes', e.target.value)}
                  className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl p-3 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-medium text-xs"
                />
              </div>

              {/* Web & Social Links */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-3.5 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-1.5">
                  <label className="block text-gray-300 font-bold uppercase text-[10px]">Website URL</label>
                  <div className="relative">
                    <Globe size={15} className="absolute left-3.5 top-3 text-gray-500" />
                    <input
                      type="url"
                      placeholder="https://yourstudio.com"
                      value={formData.website || ''}
                      onChange={(e) => handleChange('website', e.target.value)}
                      className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl pl-9 pr-3 py-2.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-medium text-xs sm:text-sm"
                    />
                  </div>
                </div>

                <div className="p-3.5 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-1.5">
                  <label className="block text-gray-300 font-bold uppercase text-[10px]">Social Handles / Bio</label>
                  <div className="relative">
                    <Share2 size={15} className="absolute left-3.5 top-3 text-gray-500" />
                    <input
                      type="text"
                      placeholder="@yourstudio | linkedin.com/in/founder"
                      value={formData.socialHandles || ''}
                      onChange={(e) => handleChange('socialHandles', e.target.value)}
                      className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl pl-9 pr-3 py-2.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-medium text-xs sm:text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-[#1F2426] flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-5 py-2.5 bg-[#0C0E0F] hover:bg-[#1F2426] text-gray-300 font-semibold rounded-xl text-xs transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold rounded-xl text-xs transition cursor-pointer flex items-center gap-2 shadow-lg active:scale-95"
                >
                  <Save size={15} />
                  <span>Save Business &amp; Invoice Profile</span>
                </button>
              </div>
            </form>
          )}

          {/* TAB: MULTI-PROVIDER AI BYOK VAULT */}
          {activeTab === 'byok' && (
            <div className="space-y-4 animate-fade-in text-xs">
              {/* Primary Active Provider Selection Box */}
              <div className="p-4 bg-[#0C0E0F] border border-indigo-500/30 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-inner">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <Key size={16} className="text-indigo-400" />
                    <span className="font-bold text-white text-sm">Primary Operational AI Engine</span>
                  </div>
                  <p className="text-xs text-gray-400">
                    Select which configured provider powers your AI Co-Founder, Bank Reconciler, and In-App Concierge.
                  </p>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <select
                    value={activeAiProvider}
                    onChange={(e) => handleSwitchAiProvider(e.target.value as AiProviderId)}
                    className="bg-[#15191C] border border-[#2D393E] text-white text-xs font-semibold rounded-xl px-3.5 py-2 focus:outline-none focus:border-indigo-500 cursor-pointer shadow-sm"
                  >
                    <option value="gemini">Google Gemini (1,500 req/day free)</option>
                    <option value="openai">OpenAI (GPT-4o &amp; o3-mini)</option>
                    <option value="anthropic">Anthropic (Claude 3.7 Sonnet)</option>
                    <option value="groq">Groq Cloud (500 tokens/sec)</option>
                    <option value="deepseek">DeepSeek (V3 &amp; R1)</option>
                    <option value="openrouter">OpenRouter (200+ models)</option>
                    <option value="mistral">Mistral AI (Large &amp; Codestral)</option>
                    <option value="perplexity">Perplexity AI (Live Sonar)</option>
                  </select>
                </div>
              </div>

              <MultiProviderBYOKCard
                onOpenModal={(pid) => {
                  if (pid) setByokInitialProvider(pid);
                  setShowByokModal(true);
                }}
              />

              <div className="p-4 bg-[#0C0E0F] border border-[#2A2F31] rounded-2xl space-y-3">
                <h4 className="text-sm font-bold text-white flex items-center gap-2">
                  <ShieldCheck size={16} className="text-emerald-400" />
                  <span>Sovereign Zero-Custody AI Guarantee</span>
                </h4>
                <p className="text-xs text-gray-400 leading-relaxed">
                  AzuraOS operates on a pure client-side Bring-Your-Own-Key (BYOK) architecture. Your API credentials and financial queries are held strictly in your browser session and never stored in server databases or utilized to train AI models.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1 text-[11px] text-gray-300">
                  <div className="p-2.5 rounded-xl bg-[#151819] border border-[#1F2426]">
                    <div className="font-bold text-white mb-0.5">Google AI Studio (Gemini)</div>
                    <div className="text-gray-400">1,500 requests/day 100% free with zero platform markup.</div>
                  </div>
                  <div className="p-2.5 rounded-xl bg-[#151819] border border-[#1F2426]">
                    <div className="font-bold text-white mb-0.5">Groq Cloud LPUs</div>
                    <div className="text-gray-400">Sub-second 500+ token/s execution on Llama 3.3 &amp; DeepSeek.</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: COHORT & ACCELERATOR SUBLICENSE */}
          {activeTab === 'cohort' && (
            <div className="animate-fade-in">
              <CohortGovernancePanel 
                showToast={showToast} 
                onOpenCohortOnboarding={() => setIsCohortModalOpen(true)}
              />
            </div>
          )}

          {/* TAB 3: TRUST DASHBOARD (CLOUD VS LOCAL AUDIT) */}
          {activeTab === 'trust_audit' && (
            <div className="animate-fade-in">
              <TrustDashboard showToast={showToast} />
            </div>
          )}

          {/* TAB 3: APP UPDATES & ACCOUNT DELETION */}
          {activeTab === 'account' && (
            <div className="space-y-4 text-xs animate-fade-in">
              {/* APP VERSION & PWA UPDATE CARD */}
              <div className="p-5 bg-[#0C0E0F] border border-[#2A2F31] rounded-2xl space-y-3 shadow-inner">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-bold text-white">AzuraOS System Version</h4>
                      <span className="px-2.5 py-0.5 rounded-full bg-[#14B8A6]/20 text-[#14B8A6] text-[10px] font-bold border border-[#14B8A6]/30">
                        v5.0 Stable
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Offline-capable Progressive Web App with auto-syncing service worker cache and local ledger vault.
                    </p>
                  </div>
                  <PWACheckUpdatesButton />
                </div>
              </div>

              {/* DELETE ACCOUNT REQUEST CARD */}
              <div className="p-5 bg-[#0C0E0F] border border-rose-500/40 rounded-2xl space-y-3.5 shadow-inner">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-rose-500/15 border border-rose-500/30 flex items-center justify-center text-rose-400 shrink-0">
                      <Trash2 size={18} />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-white">Account &amp; Data Deletion Requests</h4>
                      <p className="text-xs text-gray-400 mt-0.5">Permanently purge your account, business profile, and cloud records</p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setIsDeleteModalOpen(true)}
                    className="px-4 py-2.5 bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/40 text-rose-300 font-bold text-xs rounded-xl transition flex items-center gap-2 cursor-pointer shrink-0 active:scale-95"
                  >
                    <Trash2 size={14} />
                    <span>Delete My Account and All Details</span>
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Delete Account Confirmation Modal */}
        <DeleteAccountModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          showToast={showToast}
        />

        {/* Cohort Onboarding Modal */}
        <CohortOnboardingModal
          isOpen={isCohortModalOpen}
          onClose={() => setIsCohortModalOpen(false)}
          onActivated={() => {
            setIsCohortModalOpen(false);
            if (showToast) showToast('🎓 Cohort sponsorship active!', 'success');
          }}
          showToast={showToast}
        />

        {/* Multi-Provider BYOK Key Vault Modal */}
        <MultiProviderBYOKModal
          isOpen={showByokModal}
          onClose={() => setShowByokModal(false)}
          showToast={showToast || (() => {})}
          initialProvider={byokInitialProvider}
        />
      </div>
    </div>
  );
}
